<template>
  <div
    class="modal-overlay"
    role="dialog"
    aria-modal="true"
    :aria-labelledby="titleId"
    @click.self="$emit('cancel')"
  >
    <div class="modal">
      <h2 :id="titleId" class="modal-title">{{ titleText }}</h2>

      <p
        v-if="serverError"
        class="server-error"
        role="alert"
      >{{ serverError }}</p>

      <form class="modal-body" @submit.prevent="onSubmit">
        <div class="field">
          <label class="field-label" :for="`${uid}-name`">Name</label>
          <input
            :id="`${uid}-name`"
            v-model="form.name"
            type="text"
            class="field-input"
            :class="{ 'has-error': errors.name && touched.name }"
            placeholder="Swiss Eating Quiz"
            @blur="touched.name = true"
          >
          <p
            v-if="errors.name && touched.name"
            class="field-error"
          >{{ errors.name }}</p>
        </div>

        <div class="field">
          <label class="field-label" :for="`${uid}-slug`">Slug</label>
          <input
            :id="`${uid}-slug`"
            v-model="form.slug"
            type="text"
            class="field-input mono"
            :class="{ 'has-error': errors.slug && touched.slug }"
            placeholder="seq-main"
            :disabled="mode === 'edit'"
            @blur="touched.slug = true"
          >
          <p class="field-hint">
            Lowercase letters, digits, hyphens or underscores. Must start with
            a letter. 2–64 characters.
          </p>
          <p
            v-if="errors.slug && touched.slug"
            class="field-error"
          >{{ errors.slug }}</p>
          <p
            v-if="mode === 'edit'"
            class="field-hint dim"
          >Slug cannot be changed once a questionnaire is created.</p>
        </div>

        <div class="field">
          <label class="field-label" :for="`${uid}-description`">
            Description
            <span class="optional">(optional)</span>
          </label>
          <textarea
            :id="`${uid}-description`"
            v-model="form.description"
            class="field-input field-textarea"
            rows="3"
            placeholder="Short admin-side note about this questionnaire."
          ></textarea>
        </div>

        <div class="field">
          <label class="field-label" :for="`${uid}-status`">Status</label>
          <select
            :id="`${uid}-status`"
            v-model="form.status"
            class="field-input field-select"
          >
            <option value="draft">draft</option>
            <option value="active">active</option>
            <option value="archived">archived</option>
          </select>
        </div>
      </form>

      <div class="modal-footer">
        <button
          type="button"
          class="btn btn-secondary"
          @click="$emit('cancel')"
        >Cancel</button>
        <button
          type="button"
          class="btn btn-primary"
          @click="onSubmit"
        >{{ submitLabel }}</button>
      </div>
    </div>
  </div>
</template>

<script>
import { SLUG_REGEX } from './_data/questionnaires.js';

let uidCounter = 0;

export default {
  name: 'QuestionnaireFormModal',
  props: {
    mode: {
      type: String,
      default: 'create', // 'create' | 'edit'
      validator: v => ['create', 'edit'].includes(v),
    },
    initial: {
      type: Object,
      default: null,
    },
    serverError: {
      type: String,
      default: null,
    },
  },
  emits: ['submit', 'cancel'],
  data() {
    uidCounter += 1;
    return {
      uid: `qfm-${uidCounter}`,
      form: {
        name: this.initial?.name || '',
        slug: this.initial?.slug || '',
        description: this.initial?.description || '',
        status: this.initial?.status || 'draft',
      },
      touched: {
        name: false,
        slug: false,
      },
    };
  },
  computed: {
    titleId() {
      return `${this.uid}-title`;
    },
    titleText() {
      return this.mode === 'edit' ? 'Edit questionnaire' : 'New questionnaire';
    },
    submitLabel() {
      return this.mode === 'edit' ? 'Save changes' : 'Create questionnaire';
    },
    errors() {
      const e = {};
      const name = this.form.name.trim();
      if (!name) e.name = 'Name is required.';

      // Skip slug validation in edit mode since the field is disabled.
      if (this.mode !== 'edit') {
        const slug = this.form.slug.trim();
        if (!slug) {
          e.slug = 'Slug is required.';
        } else if (!SLUG_REGEX.test(slug)) {
          e.slug = 'Slug must be lowercase letters, digits, hyphens or underscores, and start with a letter.';
        }
      }
      return e;
    },
    isValid() {
      return Object.keys(this.errors).length === 0;
    },
  },
  methods: {
    onSubmit() {
      this.touched.name = true;
      this.touched.slug = true;
      if (!this.isValid) return;
      this.$emit('submit', {
        name: this.form.name.trim(),
        slug: this.form.slug.trim(),
        description: this.form.description.trim(),
        status: this.form.status,
      });
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  --color-bg-card: #ffffff;
  --color-bg-soft: #fafaf7;
  --color-text-primary: #2c2c2a;
  --color-text-secondary: #5f5e5a;
  --color-text-tertiary: #888780;
  --color-border: rgba(0, 0, 0, 0.12);
  --color-accent: #534AB7;
  --color-accent-soft: rgba(83, 74, 183, 0.12);
  --color-accent-dark: #3F368F;
  --color-bad: #C8392E;
  --radius-md: 8px;
  --radius-lg: 12px;
  --font-sans: 'Montserrat', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  --font-mono: "SF Mono", Menlo, Consolas, monospace;

  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  z-index: 2000;
  font-family: var(--font-sans);
}

.modal {
  background: var(--color-bg-card);
  border-radius: var(--radius-lg);
  border: 0.5px solid var(--color-border);
  padding: 1.5rem;
  max-width: 460px;
  width: 100%;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.modal-title {
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 1rem;
  color: var(--color-text-primary);
}

.server-error {
  font-size: 13px;
  color: #6B1F18;
  background: rgba(200, 57, 46, 0.10);
  border: 0.5px solid rgba(200, 57, 46, 0.45);
  border-radius: var(--radius-md);
  padding: 10px 14px;
  margin: 0 0 1rem;
  line-height: 1.5;
}

.modal-body {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  overflow-y: auto;
  margin-bottom: 1.25rem;
  padding-bottom: 4px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field-label {
  font-size: 13px;
  font-weight: 500;
  color: var(--color-text-secondary);
}

.field-label .optional {
  color: var(--color-text-tertiary);
  font-weight: 400;
  margin-left: 4px;
}

.field-input {
  width: 100%;
  padding: 10px 12px;
  border: 0.5px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-bg-card);
  color: var(--color-text-primary);
  font-family: var(--font-sans);
  font-size: 14px;
  line-height: 1.4;
}

.field-input.mono {
  font-family: var(--font-mono);
  font-size: 13px;
}

.field-input:focus {
  outline: none;
  border-color: var(--color-accent);
  box-shadow: 0 0 0 2px var(--color-accent-soft);
}

.field-input:disabled {
  background: var(--color-bg-soft);
  color: var(--color-text-tertiary);
  cursor: not-allowed;
}

.field-input.has-error {
  border-color: var(--color-bad);
  box-shadow: 0 0 0 2px rgba(200, 57, 46, 0.15);
}

.field-textarea {
  resize: vertical;
  min-height: 60px;
}

.field-select {
  appearance: auto;
}

.field-hint {
  font-size: 11px;
  color: var(--color-text-tertiary);
  margin: 0;
  line-height: 1.4;
}

.field-hint.dim {
  font-style: italic;
}

.field-error {
  font-size: 12px;
  color: var(--color-bad);
  margin: 0;
}

.modal-footer {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  padding-top: 0.5rem;
}

.btn {
  padding: 10px 18px;
  border-radius: var(--radius-md);
  font-size: 13px;
  font-family: inherit;
  font-weight: 500;
  cursor: pointer;
  border: 0.5px solid var(--color-border);
  transition: opacity 0.15s, background 0.15s, border-color 0.15s;
}

.btn-secondary {
  background: var(--color-bg-card);
  color: var(--color-text-primary);
}

.btn-secondary:hover {
  background: var(--color-bg-soft);
}

.btn-primary {
  background: var(--color-accent);
  color: white;
  border-color: var(--color-accent);
}

.btn-primary:hover {
  background: var(--color-accent-dark);
  border-color: var(--color-accent-dark);
}
</style>
